// React
import { FunctionComponent, useEffect, useRef, useState } from "react";

// HTTP
import axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";

// Components
import VideoPlayer from "src/components/video_player/VideoPlayer";

// Styles
import "./PortalVideoLinkPlayerSamsaraIncident.css";

interface Props {
  objectId: number;
}

/**
 * PortalVideoLinkPlayerSamsaraIncident
 * ------------------------------------
 * Fetches and renders two Samsara video streams (inward + forward)
 * side-by-side for the given `objectId`.
 *
 * Responsibilities:
 * - Calls `/api/samsara/video-stream` for both directions
 * - Converts blobs to object URLs for video playback
 * - Manages loading and error states separately for each video
 * - Cleans up blob URLs on unmount or `objectId` change
 */
const PortalVideoLinkPlayerSamsaraIncident: FunctionComponent<Props> = ({ objectId }) => {
  // State: video blob URLs
  const [urls, setUrls] = useState<[string | null, string | null]>([null, null]);

  // State: loading state per video
  const [loading, setLoading] = useState<[boolean, boolean]>([true, true]);

  // State: error state per video
  const [errors, setErrors] = useState<[string | null, string | null]>([null, null]);

  // Track created object URLs for cleanup
  const createdUrls = useRef<(string | null)[]>([null, null]);

  useEffect(() => {
    let cancelled = false;

    // Revoke any previously created URLs when objectId changes
    createdUrls.current.forEach(url => url && URL.revokeObjectURL(url));
    createdUrls.current = [null, null];

    // Reset component state
    setUrls([null, null]);
    setLoading([true, true]);
    setErrors([null, null]);

    /**
     * Fetch a single video stream blob for given direction
     */
    const fetchOne = async (direction: "inward" | "forward", idx: number) => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/samsara/video-stream`,
          {
            ...AxiosConfig.configValue(),
            params: { objectId, direction },
            responseType: "blob",
          }
        );

        if (cancelled) return;

        // Convert blob to object URL and store
        const url = URL.createObjectURL(res.data);
        createdUrls.current[idx] = url;

        setUrls(prev => {
          const copy = [...prev] as [string | null, string | null];
          copy[idx] = url;
          return copy;
        });
      } catch {
        if (!cancelled) {
          setErrors(prev => {
            const copy = [...prev] as [string | null, string | null];
            copy[idx] = `Failed to load ${direction} video.`;
            return copy;
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(prev => {
            const copy = [...prev] as [boolean, boolean];
            copy[idx] = false;
            return copy;
          });
        }
      }
    };

    // Fetch both inward and forward streams
    fetchOne("inward", 0);
    fetchOne("forward", 1);

    // Cleanup on unmount or `objectId` change
    return () => {
      cancelled = true;
      createdUrls.current.forEach(url => url && URL.revokeObjectURL(url));
      createdUrls.current = [null, null];
    };
  }, [objectId]);

  /**
   * Renders one side of the player grid (loading, error, or playable video)
   */
  const renderVideo = (label: string, idx: number) => {
    if (loading[idx]) {
      return (
        <div className="PortalVideoLinkPlayerSamsaraIncidentPlaceholder">
          Loading {label}…
        </div>
      );
    }

    if (errors[idx]) {
      return (
        <div className="PortalVideoLinkPlayerSamsaraIncidentPlaceholder PortalVideoLinkPlayerSamsaraIncidentError">
          {errors[idx]}
        </div>
      );
    }

    if (urls[idx]) {
      return (
        <VideoPlayer
          src={urls[idx]}
          controls
          autoPlay
          muted
          width="100%"
          height="auto"
        />
      );
    }

    return (
      <div className="PortalVideoLinkPlayerSamsaraIncidentPlaceholder PortalVideoLinkPlayerSamsaraIncidentError">
        {label} not available
      </div>
    );
  };

  return (
    <div className="PortalVideoLinkPlayerSamsaraIncident">
      <div className="PortalVideoLinkPlayerSamsaraIncidentGrid">
        <div className="PortalVideoLinkPlayerSamsaraIncidentItem">
          <h6>Inward</h6>
          {renderVideo("Inward", 0)}
        </div>
        <div className="PortalVideoLinkPlayerSamsaraIncidentItem">
          <h6>Forward</h6>
          {renderVideo("Forward", 1)}
        </div>
      </div>
    </div>
  );
};

export default PortalVideoLinkPlayerSamsaraIncident;
